// เหตุการณ์ตามภูมิภาค (docs/PLAN.md เฟส 3) ปรากฏการณ์ที่เกิดเฉพาะบางส่วนของโลก
// ภูมิภาคดูจากพิกัด (src/geo/regions.ts) ส่วนเรื่องที่ขึ้นกับประเภทพื้นที่อย่างเดียว เช่น หิมะในป่าเขตอบอุ่น อยู่ใน biomes.ts

import type { ArchetypeId } from "../../data/groups";
import type { GameEvent } from "../../engine/types";
import type { RegionId } from "../../geo/regions";

const LAND: readonly ArchetypeId[] = ["MAMMAL", "BIRD", "REPTILE", "AMPHIBIAN", "INSECT", "SOCIAL_INSECT", "ARACHNID", "MYRIAPOD", "MOLLUSC", "WORM"];
const FRESHWATER: readonly ArchetypeId[] = ["FISH", "AMPHIBIAN", "CRUSTACEAN"];
const SEA_HUNTERS: readonly ArchetypeId[] = ["FISH", "MAMMAL", "BIRD", "REPTILE", "MOLLUSC"];
const ACTIVE = ["young", "adult", "old"] as const;

// พายุหมุนเขตร้อนแบบเดียวกัน แต่เรียกชื่อต่างกันตามแหล่งที่เกิด
function storm(id: string, region: RegionId, name: string, fact: string): GameEvent {
  return {
    id,
    stageTags: ACTIVE,
    requires: { region: [region], sessile: false },
    weight: 2,
    threatenedWeight: 1.3,
    text: `${name}กำลังเคลื่อนเข้ามา ลมแรงขึ้นทุกชั่วโมง ฝนกระหน่ำไม่หยุด`,
    fact,
    choices: [
      {
        label: "หาที่หลบให้มิดชิดที่สุด",
        outcomes: [
          { chance: 0.7, text: `คุณหลบอยู่จน${name}ผ่านไป ข้างนอกเสียหายไปหมด แต่คุณรอด`, effects: { fullness: -10, safety: 5 } },
          { chance: 0.3, text: `ที่หลบของคุณพังลงกลาง${name}`, effects: { health: -25, cause: "disaster" } },
        ],
      },
      {
        label: "หนีออกจากทางพายุ",
        outcomes: [
          { chance: 0.6, text: "คุณเดินทางไกลจนพ้นแนวพายุ และได้ที่อยู่ใหม่", effects: { fullness: -15, move: "far" } },
          { chance: 0.4, text: "ลมพัดคุณไปไกลกว่าที่ตั้งใจ และบาดเจ็บระหว่างทาง", effects: { health: -20, move: "far", cause: "disaster" } },
        ],
      },
    ],
  };
}

export const REGION_EVENTS: readonly GameEvent[] = [
  // ---------- เอเชียมรสุม ----------
  {
    id: "monsoon_arrives",
    archetypes: LAND,
    stageTags: ACTIVE,
    requires: { region: ["monsoon_asia"] },
    weight: 3,
    text: "ลมมรสุมตะวันตกเฉียงใต้มาถึง ฝนแรกตกลงบนดินที่แห้งมาหลายเดือน",
    fact: "มรสุมตะวันตกเฉียงใต้นำฝนมาสู่เอเชียใต้และเอเชียตะวันออกเฉียงใต้ราวเดือนพฤษภาคมถึงตุลาคม ฝนส่วนใหญ่ของทั้งปีตกในช่วงนี้",
    choices: [
      {
        label: "ออกหากินหลังฝนแรก",
        outcomes: [
          { chance: 0.75, text: "ใบไม้แตกใหม่ แมลงออกมาเต็มไปหมด อาหารอุดมสมบูรณ์", effects: { fullness: 30 } },
          { chance: 0.25, text: "ฝนตกหนักจนน้ำป่าไหลหลาก คุณต้องหนีขึ้นที่สูง", effects: { fullness: 5, health: -10, move: "near", cause: "disaster" } },
        ],
      },
      { label: "รอให้ฝนตกต่อเนื่องก่อน", outcomes: [{ chance: 1, text: "คุณรอดูอยู่ในที่ปลอดภัย อาหารยังพอมี", effects: { fullness: 10, safety: 5 } }] },
    ],
  },
  {
    id: "monsoon_floodplain",
    archetypes: FRESHWATER,
    stageTags: ACTIVE,
    requires: { region: ["monsoon_asia"], habitat: ["freshwater", "wetland"] },
    weight: 3,
    text: "น้ำจากฝนมรสุมเอ่อล้นตลิ่ง ท่วมทุ่งนาและป่าริมน้ำกว้างสุดลูกหูลูกตา",
    fact: "ปลาน้ำจืดหลายชนิดในลุ่มน้ำโขงและเจ้าพระยาว่ายขึ้นไปวางไข่ในทุ่งที่น้ำท่วมช่วงต้นฤดูฝน ลูกปลาได้อาหารมากและมีที่หลบซ่อนเยอะ",
    choices: [
      {
        label: "ว่ายขึ้นไปในทุ่งน้ำท่วม",
        outcomes: [
          { chance: 0.7, text: "ทุ่งน้ำท่วมเต็มไปด้วยอาหาร คุณกินจนอิ่ม", effects: { fullness: 35, move: "near" } },
          { chance: 0.3, text: "น้ำลดเร็วกว่าที่คิด คุณเกือบติดอยู่ในแอ่งตื้น", effects: { fullness: 10, health: -15, cause: "starvation" } },
        ],
      },
      { label: "อยู่ในลำน้ำสายหลัก", outcomes: [{ chance: 1, text: "ลำน้ำสายหลักลึกและปลอดภัยกว่า แต่อาหารไม่มากเท่า", effects: { fullness: 15, safety: 5 } }] },
    ],
  },
  {
    id: "monsoon_dry_pool",
    archetypes: FRESHWATER,
    stageTags: ACTIVE,
    requires: { region: ["monsoon_asia"], habitat: ["freshwater", "wetland"] },
    weight: 2,
    text: "หน้าแล้งมาถึง น้ำในหนองหดเหลือแค่แอ่งโคลนเล็กๆ",
    fact: "ปลาช่อนหายใจจากอากาศได้ หน้าแล้งจึงฝังตัวอยู่ในโคลนชื้นก้นหนองรอฝนได้",
    choices: [
      {
        label: "ฝังตัวในโคลนรอฝน",
        outcomes: [
          { chance: 0.65, text: "โคลนยังชื้นพอ คุณรอจนฝนแรกมาถึง", effects: { fullness: -20, safety: 10 } },
          { chance: 0.35, text: "โคลนแห้งแตกระแหงก่อนฝนจะมา", effects: { fullness: -20, health: -30, cause: "starvation" } },
        ],
      },
      {
        label: "หาทางไปแหล่งน้ำที่ลึกกว่า",
        outcomes: [
          { chance: 0.5, text: "คุณไปถึงแหล่งน้ำที่ยังไม่แห้ง", effects: { fullness: -10, move: "near" } },
          { chance: 0.5, text: "ระหว่างทางที่น้ำตื้น นกกระยางเห็นคุณเข้า", effects: { health: -30, cause: "predator" } },
        ],
      },
    ],
  },
  {
    id: "tonle_sap_reverse",
    archetypes: FRESHWATER,
    stageTags: ACTIVE,
    requires: { region: ["tonle_sap"], habitat: ["freshwater", "wetland"] },
    weight: 4,
    text: "แม่น้ำโขงหนุนสูงขึ้นมาก จนกระแสน้ำในลำน้ำที่คุณอยู่เริ่มไหลย้อนกลับ",
    fact: "ในฤดูฝน แม่น้ำโขงหนุนสูงจนแม่น้ำทนเลสาบไหลย้อนกลับเข้าทะเลสาบเขมร ทะเลสาบขยายใหญ่ขึ้นหลายเท่าและท่วมป่าโดยรอบ จึงเป็นแหล่งปลาน้ำจืดที่อุดมที่สุดแห่งหนึ่งของโลก",
    choices: [
      {
        label: "ว่ายตามน้ำเข้าไปในทะเลสาบ",
        outcomes: [
          { chance: 0.8, text: "ป่าที่น้ำท่วมรอบทะเลสาบเต็มไปด้วยอาหาร", effects: { fullness: 35, move: "near" } },
          { chance: 0.2, text: "ชาวประมงวางเครื่องมือดักปลาไว้ตามทางน้ำ คุณหลุดรอดมาได้อย่างหวุดหวิด", effects: { health: -20, cause: "human" } },
        ],
      },
      { label: "อยู่ในแม่น้ำโขงต่อ", outcomes: [{ chance: 1, text: "คุณอยู่ในแม่น้ำสายหลักที่คุ้นเคย", effects: { fullness: 10 } }] },
    ],
  },

  // ---------- พายุหมุนเขตร้อน ----------
  storm("typhoon", "typhoon_belt", "พายุไต้ฝุ่น", "แปซิฟิกตะวันตกเฉียงเหนือเป็นแหล่งที่เกิดพายุหมุนเขตร้อนมากที่สุดในโลก ราวหนึ่งในสามของพายุหมุนเขตร้อนทั้งหมด"),
  storm("hurricane", "hurricane_belt", "พายุเฮอริเคน", "ฤดูเฮอริเคนของมหาสมุทรแอตแลนติกอย่างเป็นทางการคือ 1 มิถุนายนถึง 30 พฤศจิกายน"),
  storm("cyclone", "cyclone_belt", "พายุไซโคลน", "พายุหมุนเขตร้อนเป็นพายุชนิดเดียวกัน แต่เรียกต่างกันตามแหล่งที่เกิด คือไต้ฝุ่นในแปซิฟิกตะวันตกเฉียงเหนือ เฮอริเคนในแอตแลนติก และไซโคลนในมหาสมุทรอินเดียกับแปซิฟิกใต้"),

  // ---------- แอมะซอน ----------
  {
    id: "amazon_flooded_forest",
    archetypes: ["FISH"],
    stageTags: ACTIVE,
    requires: { region: ["amazon"], habitat: ["freshwater", "wetland"] },
    weight: 4,
    text: "แม่น้ำเอ่อล้นเข้าไปในป่า ตอนนี้คุณว่ายอยู่ระหว่างลำต้นของต้นไม้ใหญ่",
    fact: "ในฤดูน้ำหลาก ระดับน้ำในแม่น้ำแอมะซอนบางช่วงสูงขึ้นกว่า 10 เมตร ปลาบางชนิดเช่นปลาตัมบากีว่ายเข้าไปกินผลไม้ที่ร่วงจากต้นในป่าน้ำท่วม",
    choices: [
      {
        label: "เข้าไปหากินในป่าน้ำท่วม",
        outcomes: [
          { chance: 0.75, text: "ผลไม้ เมล็ด และแมลงร่วงลงน้ำไม่หยุด คุณกินจนอิ่ม", effects: { fullness: 35, move: "near" } },
          { chance: 0.25, text: "ในเงามืดใต้รากไม้มีปลาปิรันยาฝูงใหญ่รออยู่", effects: { health: -25, cause: "predator" } },
        ],
      },
      { label: "อยู่ในแม่น้ำสายหลัก", outcomes: [{ chance: 1, text: "คุณอยู่ในน้ำเปิดที่มองเห็นนักล่าได้ไกล", effects: { fullness: 10, safety: 5 } }] },
    ],
  },
  {
    id: "amazon_flood_land",
    archetypes: ["MAMMAL", "REPTILE", "AMPHIBIAN", "INSECT", "SOCIAL_INSECT", "ARACHNID", "MYRIAPOD", "WORM"],
    stageTags: ACTIVE,
    requires: { region: ["amazon"], canFly: false, sessile: false },
    weight: 3,
    text: "น้ำในแม่น้ำขึ้นสูงทุกวันจนท่วมพื้นป่า ที่แห้งเหลือน้อยลงเรื่อยๆ",
    fact: "ป่าน้ำท่วมของแอมะซอน (várzea และ igapó) จมน้ำอยู่หลายเดือนต่อปี สัตว์ที่อาศัยบนพื้นป่าต้องย้ายขึ้นต้นไม้หรือไปที่ดอน",
    choices: [
      {
        label: "ปีนขึ้นที่สูง",
        outcomes: [
          { chance: 0.7, text: "คุณรอดจากน้ำท่วม แม้บนนั้นจะแออัดไปด้วยสัตว์อื่น", effects: { fullness: -10, safety: 5 } },
          { chance: 0.3, text: "ที่สูงตรงนั้นมีนักล่าหนีน้ำมาด้วย", effects: { health: -20, cause: "predator" } },
        ],
      },
      {
        label: "เดินทางไปหาที่ดอน",
        outcomes: [
          { chance: 0.6, text: "คุณไปถึงที่ดอนที่น้ำท่วมไม่ถึง", effects: { fullness: -15, move: "near" } },
          { chance: 0.4, text: "คุณต้องลุยน้ำไกลกว่าที่คิดจนหมดแรง", effects: { health: -25, move: "near", cause: "disaster" } },
        ],
      },
    ],
  },

  // ---------- แอฟริกา ----------
  {
    id: "great_migration",
    archetypes: ["MAMMAL", "REPTILE", "BIRD", "INSECT"],
    stageTags: ACTIVE,
    requires: { region: ["serengeti"], sessile: false },
    weight: 4,
    text: "พื้นดินสั่นสะเทือน ฝูงวิลเดอบีสต์และม้าลายมากมายสุดสายตากำลังเดินผ่านมา",
    fact: "วิลเดอบีสต์กว่าหนึ่งล้านตัวเดินทางวนระหว่างเซเรงเกติในแทนซาเนียกับมาไซมาราในเคนยาทุกปี ตามฝนและหญ้าอ่อน",
    choices: [
      {
        label: "ตามฝูงไป",
        outcomes: [
          { chance: 0.6, text: "ที่ไหนมีฝูงสัตว์ ที่นั่นมีอาหาร ทั้งหญ้าอ่อน แมลง และซากสัตว์", effects: { fullness: 30, move: "far" } },
          { chance: 0.4, text: "คุณโดนฝูงสัตว์ที่วิ่งตื่นตกใจเหยียบเข้า", effects: { health: -25, cause: "accident" } },
        ],
      },
      { label: "หลบห่างจากฝูง", outcomes: [{ chance: 1, text: "คุณรอจนฝูงผ่านไป ทุ่งหญ้าถูกกินจนเหลือแต่ตอ", effects: { fullness: -5, safety: 10 } }] },
    ],
  },
  {
    id: "sardine_run",
    archetypes: SEA_HUNTERS,
    stageTags: ACTIVE,
    requires: { region: ["sardine_coast"], sessile: false },
    weight: 4,
    text: "ฝูงปลาซาร์ดีนนับไม่ถ้วนว่ายขึ้นเหนือเลียบชายฝั่ง นักล่าจากทั่วทะเลตามมา",
    fact: "ทุกปีราวเดือนพฤษภาคมถึงกรกฎาคม ปลาซาร์ดีนจำนวนมหาศาลว่ายขึ้นเหนือเลียบชายฝั่งตะวันออกของแอฟริกาใต้ เรียกว่า Sardine Run โลมา ฉลาม และนกกาเน็ตตามมาล่ากันเป็นฝูงใหญ่",
    choices: [
      {
        label: "เข้าไปร่วมวงล่า",
        outcomes: [
          { chance: 0.7, text: "ปลาซาร์ดีนถูกต้อนเป็นก้อนกลม คุณกินได้เต็มที่", effects: { fullness: 40 } },
          { chance: 0.3, text: "ในวงล่ามีฉลามตัวใหญ่ที่ไม่สนว่าใครเป็นใคร", effects: { fullness: 15, health: -25, cause: "predator" } },
        ],
      },
      { label: "อยู่ห่างๆ ไว้ก่อน", outcomes: [{ chance: 1, text: "คุณเก็บเศษปลาที่หลุดออกมาจากฝูง", effects: { fullness: 15, safety: 5 } }] },
    ],
  },

  // ---------- อเมริกาใต้ฝั่งแปซิฟิก ----------
  {
    id: "humboldt_upwelling",
    archetypes: SEA_HUNTERS,
    stageTags: ACTIVE,
    requires: { region: ["humboldt"], sessile: false },
    weight: 3,
    text: "น้ำเย็นจากทะเลลึกพุ่งขึ้นมาใกล้ผิวน้ำ ฝูงปลากะตักหนาแน่นจนน้ำเป็นสีเข้ม",
    fact: "กระแสน้ำฮัมโบลต์นอกชายฝั่งเปรูและชิลีพาน้ำเย็นที่อุดมด้วยสารอาหารขึ้นมา ที่นี่จึงเป็นแหล่งจับปลาชนิดเดียวที่ใหญ่ที่สุดในโลก คือปลากะตักเปรู",
    choices: [
      {
        label: "กินให้เต็มที่",
        outcomes: [
          { chance: 0.8, text: "อาหารมีมากจนกินไม่หมด", effects: { fullness: 35 } },
          { chance: 0.2, text: "เรือประมงก็มาที่นี่เหมือนกัน คุณติดอวนแล้วดิ้นหลุดออกมาได้", effects: { fullness: 10, health: -20, cause: "human" } },
        ],
      },
      { label: "กินพอประมาณแล้วถอยออกมา", outcomes: [{ chance: 1, text: "คุณได้อาหารพอดีและไม่เสี่ยง", effects: { fullness: 20, safety: 5 } }] },
    ],
  },
  {
    id: "el_nino",
    archetypes: SEA_HUNTERS,
    stageTags: ACTIVE,
    requires: { region: ["humboldt"], sessile: false },
    weight: 2,
    threatenedWeight: 1.3,
    text: "ปีนี้น้ำทะเลอุ่นผิดปกติ ฝูงปลาที่เคยมีมากมายหายไปเกือบหมด",
    fact: "ในปีเอลนีโญ น้ำอุ่นจากแปซิฟิกตะวันตกปิดทับน้ำเย็นที่พาสารอาหารขึ้นมา ปลากะตักลดลงมาก นกทะเลและสิงโตทะเลจำนวนมากอดตาย",
    choices: [
      {
        label: "ออกหาอาหารไกลกว่าเดิม",
        outcomes: [
          { chance: 0.55, text: "คุณเจอน้ำเย็นที่ยังมีปลาอยู่ไกลออกไป", effects: { fullness: 10, move: "far" } },
          { chance: 0.45, text: "ไปที่ไหนก็ไม่มีปลา คุณผอมลงทุกวัน", effects: { fullness: -25, health: -10, cause: "starvation" } },
        ],
      },
      { label: "ประหยัดแรงรอให้น้ำเย็นกลับมา", outcomes: [{ chance: 1, text: "คุณผ่านช่วงนั้นมาได้ แต่ผอมลงมาก", effects: { fullness: -25, safety: 5 } }] },
    ],
  },

  // ---------- ขั้วโลก ----------
  {
    id: "arctic_sea_ice",
    archetypes: ["MAMMAL", "BIRD", "FISH", "CRUSTACEAN"],
    stageTags: ACTIVE,
    requires: { region: ["arctic_sea"] },
    weight: 3,
    threatenedWeight: 1.5,
    text: "น้ำแข็งทะเลละลายเร็วกว่าที่เคย ขอบน้ำแข็งถอยห่างออกไปทุกวัน",
    fact: "พื้นที่น้ำแข็งทะเลอาร์กติกช่วงปลายฤดูร้อนลดลงมากตั้งแต่เริ่มวัดด้วยดาวเทียมในปี 1979 สัตว์ที่หากินตามขอบน้ำแข็งจึงต้องเดินทางไกลขึ้น",
    choices: [
      {
        label: "ตามขอบน้ำแข็งไป",
        outcomes: [
          { chance: 0.6, text: "ขอบน้ำแข็งยังมีอาหารเหมือนเดิม แค่ไกลขึ้น", effects: { fullness: 15, move: "far" } },
          { chance: 0.4, text: "ทางไกลกว่าที่คิด คุณหมดแรงก่อนจะถึง", effects: { fullness: -20, health: -15, cause: "starvation" } },
        ],
      },
      { label: "หากินในน้ำเปิดตรงนี้", outcomes: [{ chance: 1, text: "น้ำเปิดมีอาหารน้อยกว่า แต่ก็พอประทังชีวิต", effects: { fullness: 5 } }] },
    ],
  },
  {
    id: "krill_swarm",
    archetypes: ["MAMMAL", "BIRD", "FISH"],
    stageTags: ACTIVE,
    requires: { region: ["southern_ocean"] },
    weight: 4,
    text: "ฝูงเคยแอนตาร์กติกหนาแน่นจนน้ำทะเลกลายเป็นสีแดงอมชมพู",
    fact: "เคยแอนตาร์กติก (krill) มีมวลรวมมากที่สุดชนิดหนึ่งในบรรดาสัตว์ทั้งโลก และเป็นอาหารหลักของวาฬ แมวน้ำ เพนกวิน และปลาในมหาสมุทรใต้",
    choices: [
      {
        label: "พุ่งเข้าไปในฝูง",
        outcomes: [
          { chance: 0.8, text: "คุณกินเคยจนอิ่มแปล้", effects: { fullness: 40 } },
          { chance: 0.2, text: "แมวน้ำเสือดาวซุ่มรออยู่ใต้ฝูงเคยเหมือนกัน", effects: { fullness: 15, health: -25, cause: "predator" } },
        ],
      },
      { label: "กินตามขอบฝูง", outcomes: [{ chance: 1, text: "ขอบฝูงก็มีเคยมากพอ", effects: { fullness: 25 } }] },
    ],
  },

  // ---------- เขตอบอุ่นและเขตหนาว ----------
  {
    id: "temperate_spring",
    archetypes: LAND,
    stageTags: ACTIVE,
    requires: { region: ["temperate"] },
    weight: 3,
    text: "หิมะละลาย ต้นไม้แตกใบอ่อน แมลงและหนอนออกมาเต็มไปหมด",
    fact: "นกหลายชนิดในเขตอบอุ่นจับเวลาวางไข่ให้ลูกนกฟักออกมาตรงกับช่วงที่หนอนผีเสื้อมีมากที่สุดในฤดูใบไม้ผลิ",
    choices: [
      { label: "กินชดเชยฤดูหนาว", outcomes: [{ chance: 1, text: "หลังฤดูหนาวอันยาวนาน คุณได้กินเต็มที่อีกครั้ง", effects: { fullness: 35, health: 5 } }] },
      {
        label: "รีบหาคู่ก่อนใคร",
        outcomes: [
          { chance: 0.6, text: "คุณจองที่ดีๆ ได้ก่อนตัวอื่น", effects: { fullness: 10, safety: 10 } },
          { chance: 0.4, text: "คู่แข่งก็คิดแบบเดียวกัน คุณได้แผลจากการแย่งที่", effects: { health: -15, cause: "accident" } },
        ],
      },
    ],
  },
  {
    id: "hibernate",
    archetypes: ["MAMMAL", "REPTILE", "AMPHIBIAN", "INSECT", "ARACHNID", "MOLLUSC"],
    stageTags: ["adult", "old"],
    // สัตว์กีบ ลิง สัตว์กินเนื้อ และวาฬไม่จำศีล (หมีแค่หลับยาวโดยอุณหภูมิร่างกายลดลงไม่มาก)
    requires: { region: ["cold_north"], canFly: false, sessile: false, notSubgroups: ["mammal.ungulate", "mammal.primate", "mammal.carnivore", "mammal.whale"] },
    weight: 3,
    text: "อากาศเย็นลงทุกวัน ร่างกายของคุณเริ่มเชื่องช้า อยากหลับยาว",
    fact: "ระหว่างจำศีล อุณหภูมิร่างกายของกระรอกดินอาร์กติกลดลงต่ำกว่าจุดเยือกแข็งได้ โดยที่เลือดไม่แข็งตัว",
    choices: [
      {
        label: "ขุดโพรงแล้วจำศีล",
        outcomes: [
          { chance: 0.8, text: "คุณหลับยาวทั้งฤดูหนาว ตื่นมาตัวผอมลงแต่ปลอดภัย", effects: { fullness: -25, safety: 15 } },
          { chance: 0.2, text: "โพรงตื้นเกินไป ความหนาวแทรกลงมาถึงตัวคุณ", effects: { fullness: -25, health: -25, cause: "disaster" } },
        ],
      },
      {
        label: "ฝืนออกหากินต่อ",
        outcomes: [
          { chance: 0.45, text: "คุณยังหาอาหารได้บ้างตามที่ที่หิมะไม่ทับ", effects: { fullness: 10 } },
          { chance: 0.55, text: "อาหารหายากเกินไป และความหนาวกัดกินพลังงานของคุณ", effects: { fullness: -20, health: -15, cause: "starvation" } },
        ],
      },
    ],
  },
];
